import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { useXR, useXRInputSourceState } from "@react-three/xr";
import { HandRig } from "@/components/three/Helpers";
import { useStore } from "@/store/useStore";

const RING_MAT = {
  graphite: { color: "#3a3d44", metalness: 1, roughness: 0.34 },
  rosegold: { color: "#e3a593", metalness: 1, roughness: 0.28 },
  silver: { color: "#d7dbe0", metalness: 1, roughness: 0.2 },
};

/* On a WebXR headset with hand tracking, seat the ring on the real ring finger.
   Falls back to the procedural 3D hand on every non-XR device. */
function XRHandRing({ handedness = "right" }) {
  const variant = useStore((s) => s.variant);
  const ringRef = useRef();
  const hand = useXRInputSourceState("hand", handedness);
  const mat = RING_MAT[variant] || RING_MAT.graphite;

  useFrame((state) => {
    const xr = state.gl.xr;
    const frame = xr?.getFrame?.();
    const refSpace = xr?.getReferenceSpace?.();
    const joints = hand?.inputSource?.hand;
    const ring = ringRef.current;
    if (!ring) return;
    if (frame && refSpace && joints) {
      const joint = joints.get?.("ring-finger-phalanx-proximal");
      const pose = joint && frame.getJointPose?.(joint, refSpace);
      if (pose) {
        const p = pose.transform.position;
        const o = pose.transform.orientation;
        ring.position.set(p.x, p.y, p.z);
        ring.quaternion.set(o.x, o.y, o.z, o.w);
        ring.rotateX(Math.PI / 2);
        ring.visible = true;
        return;
      }
    }
    ring.visible = false;
  });

  return (
    <mesh ref={ringRef} visible={false}>
      <torusGeometry args={[0.0105, 0.0035, 24, 48]} />
      <meshStandardMaterial color={mat.color} metalness={mat.metalness} roughness={mat.roughness} />
    </mesh>
  );
}

export default function LotusTryOn() {
  const session = useXR((s) => s.session);
  // In an immersive session use real hand tracking; otherwise show the 3D hand.
  return session ? <XRHandRing /> : <HandRig />;
}
